/**
 * repositories/emailCategoriesRepository.ts
 * Email Categories table data access layer
 *
 * Architecture: WU1.5 Task 8 - EmailCategory Repository Pattern
 * Simplified pattern for low-volume, low-update table:
 * - Column projection for consistency
 * - No optimistic concurrency (categories rarely updated)
 * - Simple CRUD operations
 * - Comprehensive error handling
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  EmailCategory,
  EmailCategoryInsert,
  EmailCategoryUpdate
} from '@/types/supabase/EmailCategory';

// PATTERN: Import wire types from generated schema
type EmailCategoryRow = Database['public']['Tables']['email_categories']['Row'];
type EmailCategoryInsertRow = Database['public']['Tables']['email_categories']['Insert'];
type EmailCategoryUpdateRow = Database['public']['Tables']['email_categories']['Update'];

/**
 * ============================================================================
 * REPOSITORY RULES
 * ============================================================================
 *
 * 1. **Select only needed columns (PROJECTION AS DEFAULT)**
 *    - ALWAYS use explicit column lists: .select('id, name, description, ...')
 *    - Can alias columns: .select('id, createdAt:created_at')
 *    - Still run through toDomain() for consistency
 *    - Keeps queries efficient, standardizes pattern
 *
 * 2. **Always map snake⇄camel at the boundary**
 *    - Database returns snake_case (wire types)
 *    - Repository returns camelCase (domain types)
 *    - Use toDomain() for reads, toWire() for inserts, toPatch() for updates
 *
 * 3. **PARTIAL UPDATE HYGIENE: undefined = omit; null = clear column**
 *    - ⚠️ CRITICAL: Use toPatch() for ALL updates, NEVER toWire()
 *    - toPatch() strips undefined keys automatically (omit from PATCH)
 *    - Passing null will set column to NULL in database (clear value)
 *
 * 4. **No optimistic concurrency for this table**
 *    - Email categories are rarely updated (setup time only)
 *    - Low conflict risk (2-4 total rows)
 *    - Keeps implementation simple
 *
 * 5. **Error handling**
 *    - Throw on Supabase errors (let caller handle)
 *    - Return null for "not found" on single lookups
 *    - Return empty array for "not found" on list queries
 *
 * ============================================================================
 */

/**
 * Projection constant - all columns for category queries
 *
 * NOTE: Table has only 5 fields, all lightweight.
 * Could use '*' but projection maintains pattern consistency.
 */
const CATEGORY_PROJECTION = 'id,name,description,created_at,updated_at' as const;

/**
 * Email Categories Repository
 *
 * Provides type-safe data access for email_categories table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Simplified implementation for low-volume table:
 * - No pagination (only 2-4 total rows expected)
 * - No optimistic concurrency (low update frequency)
 * - Simple CRUD operations
 */
export const emailCategoriesRepository = {
  /**
   * Get category by ID
   *
   * PATTERN: Single record lookup with nullable return and column projection
   *
   * @param id - Category ID (UUID)
   * @returns Promise<EmailCategory | null> - Category if found, null otherwise
   */
  async getById(id: string): Promise<EmailCategory | null> {
    const { data, error } = await supabase
      .from('email_categories')
      .select(CATEGORY_PROJECTION)
      .eq('id', id)
      .maybeSingle<EmailCategoryRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<EmailCategory>(data);
  },

  /**
   * Get category by name
   *
   * PATTERN: Unique lookup by business key (name)
   *
   * Primary lookup method for category access. Category names are unique.
   *
   * @param name - Category name (e.g., "Beta Applications", "User Management")
   * @returns Promise<EmailCategory | null> - Category if found, null otherwise
   */
  async getByName(name: string): Promise<EmailCategory | null> {
    const { data, error } = await supabase
      .from('email_categories')
      .select(CATEGORY_PROJECTION)
      .eq('name', name)
      .maybeSingle<EmailCategoryRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<EmailCategory>(data);
  },

  /**
   * List all categories
   *
   * PATTERN: Simple list query with no pagination
   *
   * Returns all categories ordered by name. No pagination needed - table
   * has only 2-4 rows expected (Beta Applications, User Management, etc.)
   *
   * @returns Promise<EmailCategory[]> - All categories
   */
  async list(): Promise<EmailCategory[]> {
    const { data, error } = await supabase
      .from('email_categories')
      .select(CATEGORY_PROJECTION)
      .order('name');

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<EmailCategory>(row));
  },

  /**
   * Create new category
   *
   * PATTERN: Insert with wire type conversion
   *
   * NOTE: Caller should check for duplicate names before calling.
   * Service layer handles name uniqueness validation.
   *
   * @param categoryData - Category data (camelCase domain type)
   * @returns Promise<EmailCategory> - Created category
   */
  async create(categoryData: EmailCategoryInsert): Promise<EmailCategory> {
    // PATTERN: Convert domain type (camelCase) to wire type (snake_case)
    const wireData = toWire<EmailCategoryInsertRow>(categoryData);

    const { data, error } = await supabase
      .from('email_categories')
      .insert(wireData)
      .select(CATEGORY_PROJECTION)
      .single<EmailCategoryRow>();

    if (error) throw error;
    if (!data) throw new Error('Insert succeeded but no data returned');

    return toDomain<EmailCategory>(data);
  },

  /**
   * Update category
   *
   * PATTERN: Partial update with undefined stripping (no concurrency check)
   *
   * NOTE: No optimistic concurrency for this table - categories rarely updated.
   *
   * @param id - Category ID
   * @param updates - Partial category updates (camelCase domain type)
   * @returns Promise<EmailCategory> - Updated category
   */
  async update(id: string, updates: EmailCategoryUpdate): Promise<EmailCategory> {
    // PATTERN: toPatch strips undefined (omit), keeps null (clear column)
    const wireUpdates = toPatch<EmailCategoryUpdateRow>(updates);

    const { data, error } = await supabase
      .from('email_categories')
      .update(wireUpdates)
      .eq('id', id)
      .select(CATEGORY_PROJECTION)
      .maybeSingle<EmailCategoryRow>();

    if (error) throw error;

    // PATTERN: If no rows updated, category doesn't exist
    if (!data) {
      throw new Error(`Category ${id} not found`);
    }

    return toDomain<EmailCategory>(data);
  },

  /**
   * Delete category
   *
   * PATTERN: Delete by ID with void return
   *
   * WARNING: This will fail if templates reference this category (FK constraint).
   * Delete templates first, or use CASCADE (not recommended).
   *
   * @param id - Category ID to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
